import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {Action} from 'platform/components';

import {useMatch} from 'react-router-dom';

import {not} from 'ramda';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {Main, PageTab, usePermissions} from '@dms/shared';
import {TransfersList} from '@dms/warehouse/transfers';

import {buildArray, useNavigate} from 'shared/web';

import {CostCorrections} from './components/CostCorrections';
import {FastTrackIssues} from './components/FastTrackIssues';
import {IssueNoteCorrections} from './components/IssueNoteCorrections';
import {IssueNotes} from './components/IssueNotes';
import {ServiceOrderIssues} from './components/ServiceOrderIssues';
import {ServiceOrderReturns} from './components/ServiceOrderReturns';

export function ServiceOrderIssueList() {
  const navigate = useNavigate();

  const serviceOrderIssuesMatch = useMatch(warehouseRoutes.serviceOrderIssues);
  const issueNotesMatch = useMatch(warehouseRoutes.issueNotes);
  const serviceOrderReturnsMatch = useMatch(warehouseRoutes.serviceOrderReturns);
  const fastTrackIssuesMatch = useMatch(warehouseRoutes.fastTrackIssues);
  const transfersMatch = useMatch(warehouseRoutes.transfers);

  const isFastTrackFeatureFlagEnabled = isFeatureEnabled(
    featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE
  );

  const isTransferOrderFeatureFlagEnabled = isFeatureEnabled(
    featureFlags.AFS_WAREHOUSE_TRANSFER_ORDER
  );

  const [
    canCreateReturnToWarehouse,
    canReadReturnToWarehouse,
    canReadFastTrackIssues,
    canCreateFastTrackIssue,
    canCreateTransferOrder,
  ] = usePermissions({
    permissionKeys: [
      'returnToWarehouseCreate',
      'returnToWarehouseRead',
      'fastTrackIssueRead',
      'fastTrackIssueCreate',
      'transferOrderCreate',
    ],
  });

  const tabs = buildArray<PageTab>()
    .add({
      id: 'service-order-issues',
      title: i18n.t('page.warehouse.labels.serviceOrderIssues'),
      content: <ServiceOrderIssues />,
      href: warehouseRoutes.serviceOrderIssues,
    })
    .when(canReadReturnToWarehouse, {
      id: 'service-order-returns',
      title: i18n.t('page.warehouse.labels.serviceOrderReturns'),
      content: <ServiceOrderReturns />,
      href: warehouseRoutes.serviceOrderReturns,
    })
    .when(canReadFastTrackIssues && isFastTrackFeatureFlagEnabled, {
      id: 'fast-track-issues',
      title: i18n.t('page.warehouse.labels.fastTrackIssues'),
      content: <FastTrackIssues />,
      href: warehouseRoutes.fastTrackIssues,
    })
    .add({
      id: 'issue-notes',
      title: i18n.t('page.warehouse.labels.issueNotes'),
      content: <IssueNotes />,
      href: warehouseRoutes.issueNotes,
    })
    .add({
      id: 'issue-note-corrections',
      title: i18n.t('page.warehouse.labels.issueNoteCorrections'),
      content: <IssueNoteCorrections />,
      href: warehouseRoutes.issueNoteCorrections,
    })
    .add({
      id: 'cost-corrections',
      title: i18n.t('page.warehouse.labels.costCorrections'),
      content: <CostCorrections />,
      href: warehouseRoutes.costCorrections,
    })
    .when(isTransferOrderFeatureFlagEnabled, {
      id: 'transfers',
      title: i18n.t('page.warehouse.labels.transfers'),
      content: <TransfersList />,
      href: warehouseRoutes.transfers,
    });

  const actions = buildArray<Action>()
    .when(serviceOrderIssuesMatch || issueNotesMatch, {
      type: 'button',
      title: i18n.t('entity.warehouse.labels.newServiceOrderIssue'),
      onClick: () => navigate(warehouseRoutes.serviceOrderIssueCreate),
      'data-testid': testIds.warehouse.serviceOrderIssues('newServiceOrderIssue'),
    })
    .when(serviceOrderReturnsMatch, {
      isDisabled: not(canCreateReturnToWarehouse),
      type: 'buttonWithTooltip',
      title: i18n.t('page.warehouse.actions.newServiceOrderReturn'),
      onClick: () => navigate(warehouseRoutes.serviceOrderReturnsCreate),
      tooltip: {
        label: i18n.t('general.labels.noPermission'),
        isDisabled: canCreateReturnToWarehouse,
      },
      'data-testid': testIds.warehouse.serviceOrderReturns('newServiceOrderReturn'),
    })
    .when(fastTrackIssuesMatch, {
      isDisabled: not(canCreateFastTrackIssue),
      type: 'buttonWithTooltip',
      title: i18n.t('page.warehouse.actions.newFastTrackIssue'),
      onClick: () => navigate(warehouseRoutes.fastTrackIssuesCreate),
      tooltip: {
        label: i18n.t('general.labels.noPermission'),
        isDisabled: canCreateFastTrackIssue,
      },
      'data-testid': testIds.warehouse.fastTrackIssues('newFastTrackIssue'),
    })
    .when(transfersMatch && isTransferOrderFeatureFlagEnabled, {
      isDisabled: not(canCreateTransferOrder),
      type: 'buttonWithTooltip',
      title: i18n.t('page.warehouse.actions.newTransfer'),
      onClick: () => navigate(warehouseRoutes.transferOrderCreate),
      tooltip: {
        label: i18n.t('general.labels.noPermission'),
        isDisabled: canCreateTransferOrder,
      },
      'data-testid': testIds.warehouse.transfers('newTransfer'),
    });

  return (
    <>
      <Helmet title={i18n.t('module.warehouse.title')} />
      <Main isFullHeight tabs={tabs} actions={actions} />
    </>
  );
}
